/** The guide-app and chat verdicts of one booking, in words (plan "Trips panel" → "App &
 *  chat", "Staff visibility", "Chat membership") — pure, so the bookings grid's cells and
 *  the user record's Trips rows read them the same way.
 *
 *  A guide sees a booking in the app when it is published to their cache AND now is inside
 *  the app window. The publish gate is re-applied to the cache only by the 4-hourly sweep,
 *  so the two can disagree; that disagreement is named ("Cache out of date"), never hidden.
 *  Chat follows its own window; predicted times carry "≈" (the 10-minute sweep). */
import type {
  BookingChat,
  BookingInstantRange,
  BookingPublishReason,
  BookingStaffVisibility,
} from '@/api/bookings';
import { formatZoned } from '@/utils/zonedTime';

/** What a status line says with its icon:
 *  - `in` — in the app / in the chat;
 *  - `upcoming` — will be (App from …, Joins chat ≈ …);
 *  - `left` — was (Left the app …, Left chat …);
 *  - `off` — not there by rule (not published, archived, excluded, no channel, …);
 *  - `stale` — pending: the cache disagrees with the publish gate until the next sweep;
 *  - `alert` — should be there and is not (Not placed in chat). */
export type TripStatusKind = 'in' | 'upcoming' | 'left' | 'off' | 'stale' | 'alert';

export interface TripStatus {
  kind: TripStatusKind;
  text: string;
  /** The evidence, one line each, for the tooltip. */
  evidence: string[];
}

const ms = (iso: string | null | undefined) => (iso ? new Date(iso).getTime() : null);

/** "7 Oct 12:00" in the trip's zone. */
const at = (iso: string, tz: string | null) => formatZoned(iso, tz, { weekday: false, zone: false });
/** "3 Sep" in the trip's zone. */
const day = (iso: string, tz: string | null) =>
  formatZoned(iso, tz, { weekday: false, time: false, zone: false });

function range(r: BookingInstantRange | null, tz: string | null): string {
  if (!r || (!r.from && !r.to)) return 'none';
  return `${r.from ? at(r.from, tz) : 'any time'} → ${r.to ? at(r.to, tz) : 'no end'}`;
}

function zoneLine(tz: string | null): string {
  return `Times in ${tz ?? 'UTC (the tour’s zone is unknown)'}`;
}

/** The later / earlier of two optional instants. */
function latest(a: string | null | undefined, b: string | null | undefined): string | null {
  if (!a) return b ?? null;
  if (!b) return a;
  return ms(a)! >= ms(b)! ? a : b;
}
function earliest(a: string | null | undefined, b: string | null | undefined): string | null {
  if (!a) return b ?? null;
  if (!b) return a;
  return ms(a)! <= ms(b)! ? a : b;
}

export const PUBLISH_REASON_TEXT: Record<BookingPublishReason, string> = {
  not_booked: 'not booked',
  tour_hidden: 'the tour is hidden from My Trips',
  booking_hidden: 'the booking is hidden from My Trips',
  self_registration: 'a self-registration, always shown',
  guide_group: 'a guide group, always shown',
  user_exempt: 'the user is exempt from staff visibility',
  guest: 'a guest booking, always shown',
  no_role: 'no staff role, always shown',
  role_not_windowed: 'a role the staff window does not apply to',
  no_start: 'no start, always shown',
  bespoke_upcoming: 'a bespoke tour, shown any time before it starts',
  before_window: 'before the staff window',
  in_window: 'inside the staff window',
  no_end: 'started, end unknown',
  after_window: 'after the staff window',
};

const CACHE_TEXT: Record<BookingStaffVisibility['cache']['state'], string> = {
  published: 'holds the booking',
  tombstoned: 'holds a removal marker',
  missing: 'has never held it',
};

/** A staff booking past its window that the guide's cache never held (`missing`). */
export const NOT_IN_APP = 'Not in the app';

/** The guide-app line of a staff booking. */
export function guideAppStatus(
  v: BookingStaffVisibility,
  tz: string | null,
  now: Date = new Date(),
): TripStatus {
  const { publish, app_window: app, cache } = v;
  const evidence = [
    `Publish gate: ${publish.visible ? 'published' : 'not published'} — ${PUBLISH_REASON_TEXT[publish.reason]}` +
      (publish.window.from || publish.window.to ? ` (${range(publish.window, tz)})` : ''),
    `App window: ${range(app, tz)}`,
    `Guide’s cache: ${CACHE_TEXT[cache.state]}`,
    zoneLine(tz),
  ];
  const t = now.getTime();

  if (publish.visible !== (cache.state === 'published')) {
    return { kind: 'stale', text: 'Cache out of date · refreshed every 4 h', evidence };
  }

  if (!publish.visible) {
    switch (publish.reason) {
      case 'before_window': {
        const from = latest(publish.window.from, app?.from);
        return from
          ? { kind: 'upcoming', text: `App from ${at(from, tz)}`, evidence }
          : { kind: 'off', text: 'Not published · outside the staff window', evidence };
      }
      case 'after_window': {
        // A booking the guide's cache never held was never in the app — nothing to have
        // left (a removal marker, `tombstoned`, is what "Left the app" means).
        if (cache.state === 'missing') return { kind: 'off', text: NOT_IN_APP, evidence };
        const to = earliest(publish.window.to, app?.to);
        return to
          ? { kind: 'left', text: `Left the app ${day(to, tz)}`, evidence }
          : { kind: 'off', text: 'Not published · outside the staff window', evidence };
      }
      case 'not_booked':
        return { kind: 'off', text: 'Not published · not booked', evidence };
      case 'tour_hidden':
        return { kind: 'off', text: 'Not published · tour hidden from My Trips', evidence };
      case 'booking_hidden':
        return { kind: 'off', text: 'Not published · hidden from My Trips', evidence };
      default:
        return { kind: 'off', text: 'Not published', evidence };
    }
  }

  const from = ms(app?.from);
  const to = ms(app?.to);
  if (from !== null && t < from) {
    return { kind: 'upcoming', text: `App from ${at(app!.from!, tz)}`, evidence };
  }
  if (to !== null && t >= to) {
    return { kind: 'left', text: `Left the app ${day(app!.to!, tz)}`, evidence };
  }
  return {
    kind: 'in',
    text: app?.to ? `In guide app until ${at(app.to, tz)}` : 'In guide app',
    evidence,
  };
}

const CONVERSATION_TEXT: Record<BookingChat['conversations'][number]['type'], string> = {
  announcements: 'Announcements',
  guides_and_staff: 'Guides & staff',
  group_chat: 'Group chat',
  chat_with_user: 'Guest threads',
};

const MEMBERSHIP_TEXT: Record<BookingChat['conversations'][number]['participant_status'], string> = {
  active_synced: 'active',
  active_unsynced: 'active, not yet synced',
  inactive: 'archived',
  removed: 'removed',
  deleted: 'left',
};

function minutes(n: number): string {
  return n % 60 === 0 && n >= 60 ? `${n / 60} h` : `${n} min`;
}

/** The chat line of any booking (guests are not windowed). */
export function chatStatus(c: BookingChat, tz: string | null): TripStatus {
  const evidence: string[] = [];
  if (c.permanent) evidence.push('A guide group: exempt from every chat window');
  else if (c.windowed) {
    const { add_before_minutes: add, remove_after_minutes: rem } = c.window;
    evidence.push(
      `Window: ${add !== null ? `joins ${minutes(add)} before the start` : 'joins on assignment'} · ${
        rem !== null ? `leaves ${minutes(rem)} after the end` : 'stays until the chat is archived'
      }`,
    );
  } else evidence.push('Not windowed: in the chat while booked');
  if (c.channel) {
    evidence.push(
      `Channel #${c.channel.id}: ${range({ from: c.channel.starts_at, to: c.channel.ends_at }, tz)}` +
        (c.channel_count > 1 ? ` (+${c.channel_count - 1} more)` : ''),
    );
  } else if (c.channel_count > 1) {
    evidence.push(`${c.channel_count} channels`);
  }
  for (const m of c.conversations) {
    evidence.push(`${CONVERSATION_TEXT[m.type]}: ${m.count} ${MEMBERSHIP_TEXT[m.participant_status]}`);
  }
  if (c.excluded.chat) evidence.push('Excluded from the chat (booking or user)');
  if (c.excluded.group_chat) evidence.push('Excluded from the group chat (booking or user)');
  if (c.joins_at || c.leaves_at) evidence.push('≈ predicted, to the 10-minute chat sweep');
  evidence.push(zoneLine(tz));

  const noGroup = c.excluded.group_chat ? ' · no group chat' : '';
  switch (c.state) {
    case 'in_chat': {
      const tail = c.muted
        ? ' · muted'
        : c.windowed
          ? c.leaves_at
            ? ` · leaves ≈ ${at(c.leaves_at, tz)}`
            : ' · until archived'
          : '';
      return { kind: 'in', text: `In chat${tail}${noGroup}`, evidence };
    }
    case 'permanent':
      return { kind: 'in', text: `Permanent · guide group${noGroup}`, evidence };
    case 'joins':
      return {
        kind: 'upcoming',
        text: `${c.joins_at ? `Joins chat ≈ ${at(c.joins_at, tz)}` : 'Joins chat later'}${noGroup}`,
        evidence,
      };
    case 'left':
      return {
        kind: 'left',
        text: c.left_confirmed_at
          ? `Left chat · last confirmed ${day(c.left_confirmed_at, tz)}`
          : 'Left chat',
        evidence,
      };
    case 'archived':
      return { kind: 'off', text: 'Chat archived', evidence };
    case 'excluded':
      return {
        kind: 'off',
        text: 'Excluded from chat',
        evidence: c.windowed
          ? [...evidence, 'Not durable: the window sweep re-includes guides']
          : evidence,
      };
    case 'not_placed':
      return {
        kind: 'alert',
        text: 'Not placed in chat',
        evidence: [...evidence, 'Should be in the chat and is not — the booking’s Resync chat fixes it'],
      };
    case 'no_channel':
      return { kind: 'off', text: 'No chat channel', evidence };
    case 'not_booked':
      return { kind: 'off', text: 'Not in chat · not booked', evidence };
  }
}
